<template>
  <div class="station-panel-schedule-container">
    <div :class="['station-panel-schedule-list', {'station-schedule-list-with-footer': displayFooterOptions}]">

      <div class="station-schedule-searchbar-container row no-gutters" v-if="displaySearchBar">
        <label :class="['align-middle', 'm-0', displaySearchBarAddon ? 'col-10' : 'col-12']">
          <i class="far fa-search station-schedule-search-icon align-middle"></i>
          <input type="text" class="form-control station-schedule-search-bar" v-model="searchText" placeholder="Search" />
          <button class="btn bg-transparent schedule-search-cancel-button" v-if="searchText" @click.prevent="clearSearch">
            <i class="fa fa-times"></i>
          </button>
        </label>
        <div v-if="displaySearchBarAddon" class="col-2">
          <slot
            name="searchAddon"
            :data="{ groupList: filteredScheduleGroupList, panelList: flattenedPanelList, panelCount: flattenedPanelList.length, isSearchActive: !!searchText }" 
          />
        </div>
      </div>

      <div class="text-center text-danger" v-if="panelScheduleError">{{panelScheduleError}}</div>
      <h3 class="text-center" v-if="searchText">Search Results for '{{searchText}}'</h3>
      <h3 class="text-center" v-if="!searchText && (filterOptimiserAvailableOnly || filterAvailableOnly || filterIgnoreHold)">Showing Items Available/Not on Hold Only</h3>
      <Spinner v-if="scheduleLoading" />
      <slot name="panelListHeader" :data="{ groupList: filteredScheduleGroupList, panelList: flattenedPanelList, panelCount: flattenedPanelList.length, isSearchActive: !!searchText }" />

      <div v-for="group of filteredScheduleGroupList" :key="`group-${group.key}`">
        <div :class="['station-schedule-header']" :style="{'color': group.colour}">
          {{group.header}}
          <div class="station-schedule-header-badge">{{group.groupCount}}</div>
        </div>

        <transition-group name="panel-schedule-list" tag="div" class="station-optimiser-panel-draggable-container station-assembly-draggable-container station-batch-draggable-container">
          <StationScheduleRow
            :id="panel.id"
            :stockId="panel.stock.id"
            :stockName="panel.stock.name"
            :panelType="panel.type"
            :panelWidth="panel.glassWidth"
            :panelHeight="panel.glassHeight"
            :grainDirection="panel.grainDirection"
            :stockFaceSideUpType="panel.stockFaceSideUpType"
            :orderSeqNum="panel.order.sequenceNumber"
            :orderCustomer="panel.contact.name"
            :colourHighlight="group.colour"
            :dueDate="panel.dueDate"
            :statusCurrent="panel.currentTaskName"
            :statusNext="panel.nextTaskName"
            :currentStation="panel.currentStation"
            :isReady="panel.isReady"
            :isCurrentTaskInProduction="panel.isCurrentTaskInProduction"
            :isCurrentTaskAtStation="panel.isCurrentTaskAtStation"
            :panelBarcode="panel.barcode"
            :panelIndex="panel.panelIndex"
            :panelQuantity="panel.quantity"
            :onHold="panel.isOnHold"
            :isStoppedRecut="panel.isStoppedRecut"
            :isStoppedCancelled="panel.isStoppedCancelled"
            :optimiserSheetId="panel.optimiserSheetId"
            :optimiserSheetStatus="panel.optimiserSheetStatus"
            :displayPanelStationStatus="!displayOptimiserMode"
            :displayPanelOptimiserStatus="displayOptimiserMode"
            @click="panelClicked(panel)"
            :ref="`panel-${panel.id}`"
            v-for="panel of group.panelList" :key="`panel-${panel.id}`">

            <template v-slot:labels>
              <LabelBadge :labelClass="panel.dueDateDiff < 0 ? 'label-production-due-overdue' : 'label-production-due-ahead'" v-if="Math.abs(panel.dueDateDiff) > 0">{{panelDueLabelDisplay(panel)}}</LabelBadge>

              <LabelBadge v-for="(tag, index) of panel.tags" :key="`tag-${index}`" :labelColour="tag.colour" class="text-uppercase">{{tag.name}}</LabelBadge>
              
              <LabelBadge labelClass="label-order-urgent" v-if="panel.isUrgent">Urgent</LabelBadge>
              <LabelBadge labelClass="label-order-type-nonChargeableRework" v-if="panel.isRecut">RECUT</LabelBadge>
              <LabelBadge labelClass="label-order-type-nonChargeableRework" v-if="panel.isNCR">NCR</LabelBadge>
              <LabelBadge labelClass="label-order-hold" v-if="panel.isOnHold">ON HOLD</LabelBadge>
              <LabelBadge labelClass="label-production-status-recut" v-if="panel.isStoppedRecut">STOP: RECUT</LabelBadge>
              <LabelBadge labelClass="label-order-status-denied" v-if="panel.isStoppedCancelled">STOP: CANCELLED</LabelBadge>
              <LabelBadge labelClass="label-order-status-preProduction" v-if="panel.order.status == OrderStatusTypes.preProduction">PRE-PRODUCTION</LabelBadge>
              <LabelBadge labelClass="label-order-template-required" v-if="panel.isTemplateHandlingRequired">TEMPLATE REQUIRED</LabelBadge>
              <LabelBadge labelClass="label-production-transfer" v-if="panel.panelHasProductionTransfers"><i class="far fa-random"></i> TRANSFER</LabelBadge>

              <LabelBadge labelClass="label-production-panel-type-assembly" v-if="panel.type == ProductionPanelTypes.assembly">ASSEMBLY</LabelBadge>
            </template>

          </StationScheduleRow>
        </transition-group>
      </div>

      <div v-if="isEmptySchedule" class="text-center">
        <h2>{{emptyScheduleText}}</h2>
      </div>
      <div v-if="filteredScheduleGroupList.length == 0" class="text-center">
        <h2>No panels in the search result</h2>
      </div>

      <slot name="panelListFooter" :data="{ groupList: filteredScheduleGroupList, panelCount: flattenedPanelList.length, isSearchActive: !!searchText }" />

    </div>
    <div class="station-schedule-filterbar" v-if="displayFooterOptions">
      <div class="row no-gutters">
        <div class="col-5 px-1">
          <StationFilterButton v-if="!displayOptimiserMode" v-model="filterAvailableOnly" :badge="panelAvailableCount">Available Only</StationFilterButton>
          <StationFilterButton v-if="displayOptimiserMode" v-model="filterOptimiserAvailableOnly">Available Only</StationFilterButton>
        </div>
        <div class="col-3 px-1">
          <StationFilterButton v-model="filterIgnoreHold">Hide Hold</StationFilterButton>
        </div>
        <div class="col-4 px-2">
          <div class="row no-gutters station-schedule-page-area">
            <div class="col-3">
              <button class="btn btn-block btn-default" @click.prevent="setPageIndex(scheduleCurrentPage-1)" :disabled="scheduleCurrentPage == 0"><i class="far fa-angle-left"></i></button>
            </div>
            <div class="col-6 station-schedule-page-area-display">
              {{scheduleCurrentPage+1}} / {{schedulePageCount}}
            </div>
            <div class="col-3">
              <button class="btn btn-block btn-default" @click.prevent="setPageIndex(scheduleCurrentPage+1)" :disabled="scheduleCurrentPage == schedulePageCount-1"><i class="far fa-angle-right"></i></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.station-panel-schedule-container {
  width: 100%;
  height: 100%;
  position: relative;
  font-size: 13px;
}
.station-panel-schedule-list {
  position: absolute;
  width: 100%;
  top: 0;
  bottom: 0;
  overflow-y:scroll;  
}

/* Panel Transitions */
.panel-schedule-list-move {
  transition: transform 0.5s;
}
.panel-schedule-list-leave {
  transition: all 0.5s;
}
.panel-schedule-list-enter, .panel-schedule-list-leave-to {
  opacity: 0;
  transform: translateX(-300px);
}

</style>

<script>
import _ from 'lodash'
import moment from 'moment'

import Utils from '@/utils/utils'
import StationScheduleRow from '@/apps/glass-production-station/src/components/schedule/stationScheduleRow'
import StationFilterButton from '@/apps/glass-production-station/src/components/station/stationFilterButton'

import { OrderStatusTypes, ProductionPanelTypes } from '@/store/types'

export default {
  name: 'StationPanelScheduleList',
  components: {
    StationScheduleRow,
    StationFilterButton
  },
  props: {
    displaySearchBar: {
      type: Boolean,
      default: true
    },
    displaySearchBarAddon: {
      type: Boolean,
      default: false
    },
    displayFooterOptions: {
      type: Boolean,
      default: true
    },
    displayOptimiserMode: {
      type: Boolean,
      default: false
    },
    emptyScheduleText: {
      type: String,
      default: 'No panels in the schedule for this station'
    },
    filterPanelType: String,
    filterStockId: {
      type: Number,
      default: 0
    },
    scheduleList: Array,
  },
  data() {
    return {
      searchText: '',
      filterAvailableOnly: false,
      filterOptimiserAvailableOnly: false,
      filterIgnoreHold: true,   // 26/11/24 - Now on by default
      scheduleLoading: true,
      panelScheduleError: '',
      panelScheduleData: {},
      panelAvailableCount: 0,

      schedulePageCount: 0,
      scheduleOffset: 0,
      schedulePageLength: 50,
      scheduleCurrentPage: 0,
      scheduleCurDraw: 0,
      scheduleRecvDraw: 0,

      scheduleListRequiresUpdate: true
    }
  },
  watch: {
    filterAvailableOnly: function() {
      this.scheduleCurrentPage = 0
      this.scheduleLoading = true
      this.debouncedSearchPanelScheduleList()
    },
    filterOptimiserAvailableOnly: function() {
      this.scheduleCurrentPage = 0
      this.scheduleLoading = true
      this.debouncedSearchPanelScheduleList()
    },
    filterIgnoreHold: function() {
      this.scheduleCurrentPage = 0
      this.scheduleLoading = true
      this.debouncedSearchPanelScheduleList()
    },
    searchText: function() {
      this.scheduleCurrentPage = 0
      this.scheduleLoading = true
      this.debouncedSearchPanelScheduleList()
    }
  },
  computed: {
    isEmptySchedule() {
      return (!this.panelScheduleData.groupList || this.panelScheduleData.groupList.length == 0)      
    },
    filteredScheduleGroupList() {
      var currentDate = moment().local()
      let urgentColour = '#EB4D4B'
      let todayColour = '#FBC530'
      let goodColour = '#4CD137'

      if(!this.panelScheduleData.groupList)
        return []

      var outputList = this.panelScheduleData.groupList.map(scheduleGroup => {
        
        var groupColour = '#4CD137'
        var groupHeader = ''

        if(scheduleGroup.options.isDespatchGroup) {
          groupHeader = 'DESPATCH'
        }
        else if(scheduleGroup.options.isCancelledGroup) {
          groupColour = todayColour
          groupHeader = 'CANCELLED'
        }
        else if(scheduleGroup.options.isCompletedGroup) {
          groupColour = goodColour
          groupHeader = 'COMPLETED'
        }
        else if(scheduleGroup.options.isIssueGroup) {
          groupColour = urgentColour
          groupHeader = 'ISSUES'
        }
        else if(scheduleGroup.options.isNCRGroup) {
          groupColour = urgentColour
          groupHeader = 'NCR'
        }
        else if(scheduleGroup.options.isOverdueGroup) {
          groupColour = urgentColour
          groupHeader = 'OVERDUE'
        }
        else {
          let groupDate = moment(scheduleGroup.options.date).local()
          
          // Today?
          if(groupDate.isSame(currentDate, 'day')) {
            groupColour = todayColour
            groupHeader = `TODAY - ${groupDate.format('Do MMM')}`
          } else {
            var dayName = groupDate.format('dddd')
            if(groupDate.isSame(currentDate.clone().add(1, 'days'), 'day'))
              dayName = 'Tomorrow'

            groupColour = goodColour
            groupHeader = `${dayName} - ${groupDate.format('Do MMM')}`
          }
        }

        return {
          header: groupHeader.toUpperCase(),
          colour: groupColour,
          key: scheduleGroup.key,
          groupCount: scheduleGroup.groupCount,
          panelList: scheduleGroup.panelList
        }
      })

      return outputList
    },
    flattenedPanelList() {
      let panelList = []
      this.filteredScheduleGroupList.forEach(group => {
        panelList = _.concat(panelList, group.panelList)
      })
      return panelList
    }
  },
  created() {
    this.debouncedSearchPanelScheduleList = _.debounce(this.requestPanelScheduleList, 250)
    this.debouncedNavigatePanelScheduleList = _.debounce(this.requestPanelScheduleList, 100)
    this.OrderStatusTypes = OrderStatusTypes
    this.ProductionPanelTypes = ProductionPanelTypes
  },
  mounted() {
    this.scheduleLoading = true
    this.requestPanelScheduleList()

    // Setup a timer to refresh the schedule list every x seconds
    // We add a random offset so all stations do not send a request all at once which is impacting server performance
    this.$options.scheduleUpdateTimer = setInterval(this.updateScheduleRefreshTimer, (7.5 + _.random(-2.5, 2.5)) * 1000)

    // Subscribe to the generic panel schedule list (virtual endpoint, not actual one)
    // Any updates to production / orders will refresh this endpoint
    this.subscribeEndpoint('/production/panelschedule', () => {
      this.scheduleListRequiresUpdate = true
    })
  },
  beforeDestroy() {
    clearInterval(this.$options.scheduleUpdateTimer)
    this.unsubscribeEndpoint('/production/panelschedule')
  },
  methods: {
    clearSearch() {
      this.searchText = ''
    },
    panelClicked(panel) {
      let panelComponent = this.$refs[`panel-${panel.id}`][0]

      this.$emit('panelClick', { panel: panel, target: panelComponent.$el })
    },
    setPageIndex(index) {
      if(index < 0 || index >= this.schedulePageCount)
        return

      this.scheduleLoading = true
      this.scheduleCurrentPage = index
      this.debouncedNavigatePanelScheduleList()
    },
    updateScheduleRefreshTimer() {
      if(this.scheduleListRequiresUpdate) {
        this.requestPanelScheduleList()
      }
    },

    // Reload the panel schedule list
    // Called externally manually as required
    reloadPanelScheduleList() {
      this.requestPanelScheduleList()
    },

    // Load the panel schedule list
    requestPanelScheduleList() {

      // Note: We don't set loading here explicitly as this function will be called very often due to panel update notifications via socket service
      // Only set loading when explicitly requested (search, page change, etc)
      this.scheduleOffset = this.scheduleCurrentPage * this.schedulePageLength
      this.scheduleListRequiresUpdate = false

      var params = {
        start: this.scheduleOffset,
        length: this.schedulePageLength,
        draw: this.scheduleCurDraw++,
        isAvailableOnly: this.filterAvailableOnly ? 1 : 0,
        isOptimiserAvailableOnly: this.filterOptimiserAvailableOnly ? 1 : 0,
        ignoreOnHold: this.filterIgnoreHold ? 1 : 0,
        searchString: this.searchText != '' ? encodeURIComponent(this.searchText) : null,
        stockId: this.filterStockId,
        panelType: this.filterPanelType,
      }

      this.panelScheduleError = ''
      this.$apiService.glassProductionStationService.getPanelScheduleForStation(this.$route.params.id, params)
      .then(response => {
        // Discard results if the data is older than newer data we have
        if(parseInt(response.data.draw) >= this.scheduleRecvDraw) {
          this.panelScheduleData = response.data

          this.schedulePageCount = Math.ceil(this.panelScheduleData.totalPanels / this.schedulePageLength)

          this.scheduleCurrentPage = this.scheduleOffset / this.schedulePageLength
          this.scheduleCurrentPage = Math.min(this.scheduleCurrentPage, this.schedulePageCount-1)
          this.scheduleCurrentPage = Math.max(this.scheduleCurrentPage, 0)

          this.panelAvailableCount = this.panelScheduleData.totalPanelsAvailable

          this.scheduleRecvDraw = parseInt(response.data.draw)
          this.scheduleCurDraw = Math.max(this.scheduleCurDraw, this.scheduleRecvDraw)

          this.$emit('urgentCountUpdated', this.panelScheduleData.totalPanelsFlagged)
        }
      })
      .catch(err => {
        this.panelScheduleError = 'Error loading panel schedule'
      })
      .finally(() => {
        this.scheduleLoading = false
      })
    },
    panelDueLabelDisplay(panel) {
      let maxDaysDisplay = 15

      if(panel.dueDateDiff < 0) {
        return `${Math.abs(panel.dueDateDiff)}${Math.abs(panel.dueDateDiff) == maxDaysDisplay ? '+' : ''} DAY` + (Math.abs(panel.dueDateDiff) == 1 ? '' : 'S')
      } else {
        return `${panel.dueDateDiff} DAY` + (panel.dueDateDiff == 1 ? '' : 'S')
      }
    }
  }
}
</script>